import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Lock,
  Mail,
  AlertCircle,
  Shield,
  ShieldCheck,
  Wifi,
  KeyRound,
  Camera,
  CameraOff,
  Eye,
  Fingerprint,
  ZoomIn,
  ZoomOut,
  Lightbulb,
  LightbulbOff,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const LOGO_KEY = 'https://customer-assets.emergentagent.com/job_2c74cda5-f28e-477a-a538-792d83f7b37f/artifacts/9pieqpxz_ShireAdvisors_Logo-Key.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Level-2 login modal: email + password + live webcam snapshot.
 * The captured frame is sent to /api/analytics/login-snapshot for audit.
 * If the user denies camera access, login still proceeds (degrade gracefully).
 */
const Level2LoginModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [clientIp, setClientIp] = useState('');
  const [cameraStatus, setCameraStatus] = useState('starting'); // starting | live | denied | unavailable
  const [snapshotTaken, setSnapshotTaken] = useState(false);
  // Camera controls
  const [zoom, setZoom] = useState(1);          // 1.0 – 3.0 (digital) or hardware range
  const [zoomCaps, setZoomCaps] = useState(null); // { min, max, step } if hardware-supported
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [lightBoost, setLightBoost] = useState(false); // CSS brightness fallback

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const { login } = useAuth();

  // Boot camera + IP fetch when modal opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    setCameraStatus('starting');
    setSnapshotTaken(false);
    setError('');
    setZoom(1);
    setZoomCaps(null);
    setTorchOn(false);
    setTorchSupported(false);
    setLightBoost(false);

    axios
      .get(`${API}/analytics/whoami`)
      .then((res) => {
        if (!cancelled) setClientIp(res.data?.ip || '');
      })
      .catch(() => {});

    if (
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== 'function'
    ) {
      setCameraStatus('unavailable');
    } else {
      navigator.mediaDevices
        .getUserMedia({ video: { width: 640, height: 480, facingMode: 'user' } })
        .then((stream) => {
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          streamRef.current = stream;
          setCameraStatus('live');
          // The <video> element doesn't exist until cameraStatus === 'live',
          // so we attach the stream in a separate effect (see below).
        })
        .catch(() => {
          if (!cancelled) setCameraStatus('denied');
        });
    }

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [isOpen]);

  // Attach the captured MediaStream to the <video> element AFTER it mounts
  // (the element is rendered conditionally on cameraStatus === 'live').
  useEffect(() => {
    if (cameraStatus !== 'live') return;
    const v = videoRef.current;
    const s = streamRef.current;
    if (!v || !s) return;
    if (v.srcObject !== s) v.srcObject = s;
    const playPromise = v.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        /* autoplay restrictions — user can click video to start */
      });
    }

    // Probe hardware zoom / torch capabilities on the video track.
    const track = s.getVideoTracks()[0];
    if (track && typeof track.getCapabilities === 'function') {
      try {
        const caps = track.getCapabilities() || {};
        if (typeof caps.zoom === 'object' && caps.zoom) {
          setZoomCaps({
            min: caps.zoom.min ?? 1,
            max: caps.zoom.max ?? 3,
            step: caps.zoom.step ?? 0.1,
          });
          const settings = track.getSettings ? track.getSettings() : {};
          if (typeof settings.zoom === 'number') setZoom(settings.zoom);
        }
        if (caps.torch === true) {
          setTorchSupported(true);
        }
      } catch {
        /* ignore — capabilities not supported */
      }
    }
  }, [cameraStatus]);

  const captureSnapshot = () => {
    if (cameraStatus !== 'live' || !videoRef.current) return null;
    try {
      const v = videoRef.current;
      const canvas = document.createElement('canvas');
      const w = v.videoWidth || 640;
      const h = v.videoHeight || 480;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      // Apply the same brightness boost the user sees if the light is on
      if (lightBoost) ctx.filter = 'brightness(1.4) contrast(1.05)';
      // Apply digital zoom by drawing a centered cropped region scaled up
      const isHwZoom = !!zoomCaps;
      if (!isHwZoom && zoom > 1) {
        const cw = w / zoom;
        const ch = h / zoom;
        const cx = (w - cw) / 2;
        const cy = (h - ch) / 2;
        ctx.drawImage(v, cx, cy, cw, ch, 0, 0, w, h);
      } else {
        ctx.drawImage(v, 0, 0, w, h);
      }
      return canvas.toDataURL('image/jpeg', 0.72);
    } catch {
      return null;
    }
  };

  // ---- Zoom controls -------------------------------------------------------
  const ZOOM_MIN = zoomCaps?.min ?? 1;
  const ZOOM_MAX = zoomCaps?.max ?? 3;
  const ZOOM_STEP = zoomCaps?.step ?? 0.25;

  const setZoomLevel = (next) => {
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, next));
    setZoom(clamped);
    if (zoomCaps && streamRef.current) {
      const track = streamRef.current.getVideoTracks()[0];
      if (track) {
        track
          .applyConstraints({ advanced: [{ zoom: clamped }] })
          .catch(() => {
            /* silently fall back to CSS digital zoom */
          });
      }
    }
  };

  // ---- Torch / light boost -------------------------------------------------
  const toggleLight = () => {
    if (torchSupported && streamRef.current) {
      const track = streamRef.current.getVideoTracks()[0];
      const next = !torchOn;
      track
        .applyConstraints({ advanced: [{ torch: next }] })
        .then(() => setTorchOn(next))
        .catch(() => {
          // Hardware torch failed — fall back to brightness boost
          setTorchSupported(false);
          setLightBoost((b) => !b);
        });
    } else {
      setLightBoost((b) => !b);
    }
  };
  const lightActive = torchOn || lightBoost;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Capture snapshot first so it's available even if login fails
    const photo = captureSnapshot();
    if (photo) {
      setSnapshotTaken(true);
      // Fire-and-forget audit; don't block login on failure
      axios
        .post(`${API}/analytics/login-snapshot`, {
          email,
          photo,
          note: 'level2-login-attempt',
        })
        .catch(() => {});
    }

    try {
      await login(email, password);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isSecureConnection =
    typeof window !== 'undefined' && window.location.protocol === 'https:';

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center"
      data-testid="level2-login-modal"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
      />

      <div
        className="relative bg-slate-950 text-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden border border-cyan-400/30"
        style={{
          boxShadow:
            '0 0 60px rgba(34,211,238,0.18), 0 0 140px rgba(34,211,238,0.1)',
        }}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/5 bg-gradient-to-br from-slate-900 via-slate-950 to-cyan-950/40 relative">
          {/* Top chips */}
          <div className="flex items-center justify-between mb-4">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-500/15 border border-cyan-400/40 text-cyan-300 text-[10px] font-mono uppercase tracking-[0.2em]">
              <Fingerprint className="w-3 h-3" />
              Level 2
            </div>
            {isSecureConnection && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-[10px] font-mono uppercase tracking-[0.2em]">
                <ShieldCheck className="w-3 h-3" />
                Secure
              </div>
            )}
          </div>

          <h2 className="text-2xl font-serif font-bold flex items-center gap-3">
            <Eye className="w-6 h-6 text-cyan-400" />
            Level 2 Access
          </h2>
          <p className="text-white/60 text-sm mt-1">
            Identity verification &amp; biometric audit
          </p>
        </div>

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
          data-testid="level2-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Camera preview */}
        <div className="px-6 pt-5">
          <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-slate-900 border border-cyan-400/20">
            {cameraStatus === 'live' ? (
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover transition-transform duration-200"
                style={{
                  // Apply CSS digital zoom only when the device doesn't support
                  // hardware zoom. With hardware zoom, the track itself scales.
                  transform: zoomCaps ? 'none' : `scale(${zoom})`,
                  transformOrigin: 'center center',
                  filter: lightBoost ? 'brightness(1.4) contrast(1.05)' : 'none',
                }}
                data-testid="level2-camera-video"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-center px-5 text-slate-400">
                {cameraStatus === 'starting' && (
                  <>
                    <Camera className="w-10 h-10 mb-3 text-cyan-400 animate-pulse" />
                    <span className="text-sm">Initializing camera…</span>
                  </>
                )}
                {cameraStatus === 'denied' && (
                  <>
                    <CameraOff className="w-10 h-10 mb-3 text-amber-400" />
                    <span className="text-sm font-semibold text-amber-300">
                      Camera access denied
                    </span>
                    <span className="text-xs text-slate-500 mt-1">
                      Sign-in will proceed without a snapshot.
                    </span>
                  </>
                )}
                {cameraStatus === 'unavailable' && (
                  <>
                    <CameraOff className="w-10 h-10 mb-3 text-slate-500" />
                    <span className="text-sm">Camera not available on this device</span>
                  </>
                )}
              </div>
            )}

            {/* Scan overlay */}
            {cameraStatus === 'live' && (
              <>
                <div className="absolute inset-0 pointer-events-none border-2 border-cyan-400/30 rounded-xl" />
                <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-red-500/80 text-white text-[10px] font-mono uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                  Rec
                </div>
                <div className="absolute bottom-2 right-2 font-mono text-[10px] text-cyan-300/80 bg-slate-900/70 px-2 py-0.5 rounded">
                  AUDIT · {new Date().toISOString().slice(11, 19)}
                </div>

                {/* Camera control bar */}
                <div
                  className="absolute bottom-2 left-2 flex items-center gap-1.5 bg-slate-900/80 backdrop-blur-sm border border-cyan-400/30 rounded-full p-1"
                  data-testid="level2-camera-controls"
                >
                  <button
                    type="button"
                    onClick={() => setZoomLevel(zoom - ZOOM_STEP)}
                    disabled={zoom <= ZOOM_MIN + 0.001}
                    className="w-7 h-7 flex items-center justify-center rounded-full text-cyan-300 hover:bg-cyan-400/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Zoom out"
                    data-testid="level2-zoom-out"
                  >
                    <ZoomOut className="w-3.5 h-3.5" />
                  </button>
                  <span
                    className="font-mono text-[10px] text-cyan-200 min-w-[34px] text-center tabular-nums"
                    data-testid="level2-zoom-level"
                  >
                    {zoom.toFixed(zoomCaps && zoomCaps.step < 0.1 ? 2 : 1)}x
                  </span>
                  <button
                    type="button"
                    onClick={() => setZoomLevel(zoom + ZOOM_STEP)}
                    disabled={zoom >= ZOOM_MAX - 0.001}
                    className="w-7 h-7 flex items-center justify-center rounded-full text-cyan-300 hover:bg-cyan-400/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Zoom in"
                    data-testid="level2-zoom-in"
                  >
                    <ZoomIn className="w-3.5 h-3.5" />
                  </button>
                  <span className="w-px h-4 bg-cyan-400/30 mx-0.5" />
                  <button
                    type="button"
                    onClick={toggleLight}
                    className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors ${
                      lightActive
                        ? 'bg-amber-400 text-slate-900'
                        : 'text-cyan-300 hover:bg-cyan-400/20'
                    }`}
                    title={lightActive ? 'Turn light off' : 'Turn light on'}
                    data-testid="level2-light-toggle"
                  >
                    {lightActive ? (
                      <Lightbulb className="w-3.5 h-3.5" />
                    ) : (
                      <LightbulbOff className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              </>
            )}
          </div>

          {snapshotTaken && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-emerald-400">
              <ShieldCheck className="w-3 h-3" />
              Snapshot captured
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 pt-5 pb-2 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-cyan-300/80 mb-1.5">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-9 bg-slate-900 border-slate-700 text-white placeholder-slate-500 focus-visible:ring-cyan-400"
                placeholder="you@example.com"
                required
                data-testid="level2-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-cyan-300/80 mb-1.5">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-9 bg-slate-900 border-slate-700 text-white placeholder-slate-500 focus-visible:ring-cyan-400"
                placeholder="••••••••"
                required
                data-testid="level2-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-cyan-500 to-sky-500 hover:from-cyan-400 hover:to-sky-400 text-slate-950 font-bold py-3 rounded-lg transition-all"
            data-testid="level2-submit-button"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
                Verifying…
              </span>
            ) : (
              <span className="inline-flex items-center justify-center gap-2">
                <Camera className="w-4 h-4" />
                Capture &amp; Sign In
              </span>
            )}
          </Button>
        </form>

        {/* Security strip */}
        <div className="px-6 pb-5 pt-3 border-t border-white/5 mt-4">
          <div className="grid grid-cols-4 gap-2 text-[10px] uppercase tracking-wider font-mono text-slate-400">
            <div className="flex items-center gap-1.5" title="TLS 1.3">
              <Shield className="w-3.5 h-3.5 text-emerald-500" />
              TLS
            </div>
            <div className="flex items-center gap-1.5" title="Bcrypt password hashing">
              <Lock className="w-3.5 h-3.5 text-emerald-500" />
              Bcrypt
            </div>
            <div className="flex items-center gap-1.5" title="JWT session token">
              <KeyRound className="w-3.5 h-3.5 text-emerald-500" />
              JWT
            </div>
            <div className="flex items-center gap-1.5" title="Camera audit snapshot">
              <Eye className="w-3.5 h-3.5 text-cyan-400" />
              Bio
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500 font-mono">
            <span className="inline-flex items-center gap-1.5">
              <Wifi className="w-3.5 h-3.5" />
              Connecting from
            </span>
            <span className="text-cyan-300 font-semibold tracking-wide">
              {clientIp || '—'}
            </span>
          </div>
        </div>

        {/* Decorative ring matching the trigger logo */}
        <img
          src={LOGO_KEY}
          alt=""
          className="absolute -bottom-12 -right-12 w-40 h-40 opacity-[0.06] pointer-events-none"
        />
      </div>
    </div>
  );
};

export default Level2LoginModal;
